import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'MbheTech Solutions';
const DEFAULT_IMAGE = '/M logo.png';
const DEFAULT_AUTHOR = 'MbheTech Solutions';

const routeMeta = {
  '/': {
    title: 'Web Development South Africa | MbheTech Solutions',
    description:
      'MbheTech Solutions delivers affordable website design, IT solutions, and SEO services in South Africa for businesses, NGOs, startups, and individuals.',
    keywords:
      'Web development South Africa, affordable website design, IT solutions Limpopo, SEO services South Africa, Tzaneen web developer, Nkowakowa IT services',
  },
  '/about': {
    title: 'About MbheTech Solutions | IT Experts in Limpopo',
    description:
      'Learn about MbheTech Solutions, a South African digital services company helping businesses grow with web development, IT consulting, and secure technology solutions.',
    keywords:
      'About MbheTech Solutions, IT company Limpopo, web development Tzaneen, South Africa technology partner',
  },
  '/services': {
    title: 'Services | Web Design, IT & SEO Services South Africa',
    description:
      'Explore MbheTech services: web development, app development, UI/UX, cloud and cybersecurity, IT consulting, and SEO services across South Africa.',
    keywords:
      'SEO services South Africa, affordable website design, IT solutions Limpopo, web design services Tzaneen',
  },
  '/systems': {
    title: 'Systems We Build | Custom Software for Business & Government',
    description:
      'MbheTech builds custom software systems for accounting firms, law firms, government institutions, and companies — queue management, case management, client portals, inventory, and more.',
    keywords:
      'custom software South Africa, business systems Limpopo, government software, law firm case management, accounting client portal, queue booking system',
  },
  '/contact': {
    title: 'Book a Consultation | MbheTech Solutions',
    description:
      'Schedule a free consultation with MbheTech Solutions for web development, IT solutions, and digital strategy in South Africa.',
    keywords:
      'book consultation web developer South Africa, IT solutions Limpopo, MbheTech meeting, schedule call',
  },
  '/pricing': {
    title: 'Affordable Website Design Pricing | MbheTech Solutions',
    description:
      'View transparent MbheTech pricing for affordable website design, business websites, and custom digital solutions in South Africa.',
    keywords:
      'affordable website design pricing, web development packages South Africa, IT services pricing Limpopo',
  },
  '/payment': {
    title: 'Secure PayFast Payment | MbheTech Solutions',
    description:
      'Complete your MbheTech website plan payment securely via PayFast. Pay your 50% deposit and activate your monthly subscription.',
    keywords: 'MbheTech PayFast payment, website deposit South Africa, secure checkout',
  },
  '/blog': {
    title: 'MbheTech Blog | Web, IT & SEO Insights South Africa',
    description:
      'Read practical guides on web development, cybersecurity, cloud, AI, and SEO services for growing businesses in South Africa.',
    keywords:
      'web development blog South Africa, IT solutions insights, SEO tips South Africa',
  },
  '/why-us': {
    title: 'Why Choose MbheTech Solutions | South African IT Partner',
    description:
      'Discover why organizations choose MbheTech Solutions for high-quality web development, IT consulting, and measurable digital growth.',
    keywords:
      'why choose MbheTech, best web development South Africa, IT consulting Limpopo',
  },
};

const getMetaForPath = (pathname) => {
  if (pathname.startsWith('/plans/')) {
    return {
      title: 'Plan Details | MbheTech Solutions',
      description:
        'Review MbheTech website and digital service plans designed for startups, NGOs, and growing businesses in South Africa.',
      keywords:
        'website plans South Africa, digital packages Limpopo, web development plans',
    };
  }

  return (
    routeMeta[pathname] || {
      title: 'MbheTech Solutions | Web Development & IT Services South Africa',
      description:
        'MbheTech Solutions provides web development, IT consulting, cloud, cybersecurity, and digital services across South Africa.',
      keywords:
        'Web development South Africa, IT solutions, SEO services South Africa',
    }
  );
};

const setMetaTag = (selector, attributeName, attributeValue, content) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setLinkTag = (selector, rel, href) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(data);
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const siteUrl = (process.env.REACT_APP_SITE_URL || 'https://www.mbhetech.co.za').replace(/\/$/, '');
    const pathname = location.pathname || '/';
    const currentUrl = `${siteUrl}${pathname}`;
    const meta = getMetaForPath(pathname);

    document.title = meta.title;
    document.documentElement.lang = 'en-ZA';

    setMetaTag('meta[name="description"]', 'name', 'description', meta.description);
    setMetaTag('meta[name="keywords"]', 'name', 'keywords', meta.keywords);
    setMetaTag('meta[name="author"]', 'name', 'author', DEFAULT_AUTHOR);
    setMetaTag('meta[name="robots"]', 'name', 'robots', 'index, follow, max-image-preview:large');

    setMetaTag('meta[property="og:title"]', 'property', 'og:title', meta.title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', meta.description);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', currentUrl);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', `${siteUrl}${DEFAULT_IMAGE}`);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
    setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', 'en_ZA');

    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', `${siteUrl}${DEFAULT_IMAGE}`);

    setLinkTag('link[rel="canonical"]', 'canonical', currentUrl);

    if (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION) {
      setMetaTag(
        'meta[name="google-site-verification"]',
        'name',
        'google-site-verification',
        process.env.REACT_APP_GOOGLE_SITE_VERIFICATION
      );
    }

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: SITE_NAME,
      image: `${siteUrl}${DEFAULT_IMAGE}`,
      url: siteUrl,
      telephone: '+27 83 283 2639',
      email: 'info@mbhetech.co.za',
      description:
        'MbheTech Solutions offers web development, affordable website design, SEO services, and IT solutions in South Africa.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Nkowakowa',
        addressLocality: 'Tzaneen',
        addressRegion: 'Limpopo',
        addressCountry: 'ZA',
      },
      areaServed: ['South Africa', 'Limpopo', 'Tzaneen', 'Nkowakowa'],
      sameAs: ['https://www.linkedin.com/company/mbhetech-solutions/'],
      priceRange: 'R2,999 - R12,999',
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: meta.title,
      description: meta.description,
      url: currentUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: siteUrl,
      },
      inLanguage: 'en-ZA',
    };

    setJsonLd('ld-json-localbusiness', localBusinessSchema);
    setJsonLd('ld-json-webpage', webPageSchema);
  }, [location.pathname]);

  return null;
};

export default SeoManager;
